import { useState } from "react";
import { Link } from "react-router-dom";
import { Plane, Building2, Car, Ticket, ArrowRight, Mail } from "lucide-react";

const TYPE_ICONS = { flight: Plane, hotel: Building2, car: Car, event: Ticket };

function CountChips({ counts }) {
  return (
    <div className="flex items-center gap-2.5">
      {Object.entries(counts).filter(([, n]) => n > 0).map(([type, n]) => {
        const Icon = TYPE_ICONS[type];
        return (
          <span key={type} className="flex items-center gap-1 font-mono text-xs text-sand-500">
            <Icon size={13} className="text-sand-400" />{n}
          </span>
        );
      })}
    </div>
  );
}

function Badges({ planned, confirmed }) {
  return (
    <div className="flex items-center gap-1.5">
      {confirmed > 0 && (
        <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#DFE9DC] text-success border border-[#C9DCC7]">
          {confirmed} conf
        </span>
      )}
      {planned > 0 && (
        <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#F3E7D3] text-[#B07B2E] border border-[#E9D5AE]">
          {planned} plan
        </span>
      )}
    </div>
  );
}

function Row({ trip }) {
  return (
    <Link
      to={`/trips/${trip.id}`}
      className="grid grid-cols-[auto_1fr_auto] sm:grid-cols-[150px_1fr_auto_auto_20px] items-center gap-3 sm:gap-4 px-5 py-4 hover:bg-sand-50 transition-colors group"
      data-testid={`trip-row-${trip.id}`}
    >
      <span className="font-mono text-xs text-sand-500 whitespace-nowrap">
        {trip.start_date || "—"}{trip.end_date ? ` →${trip.end_date.slice(5)}` : ""}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-sand-950 truncate group-hover:text-ember transition-colors">{trip.name}</p>
        {trip.destination && <p className="text-xs text-sand-500 truncate">{trip.destination}</p>}
      </div>
      <div className="hidden sm:block"><CountChips counts={trip.res_counts} /></div>
      <Badges planned={trip.planned} confirmed={trip.confirmed} />
      <ArrowRight size={14} className="hidden sm:block text-sand-300 group-hover:text-ember group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}

export default function TripRows({ trips, today }) {
  const [showPast, setShowPast] = useState(false);
  const upcoming = trips.filter((t) => !t.end_date || t.end_date >= today);
  const past = trips.filter((t) => t.end_date && t.end_date < today).reverse();

  return (
    <div className="rounded-card border border-hairline bg-surface" data-testid="trip-rows">
      <div className="px-5 py-4 border-b border-sand-200 flex items-center justify-between">
        <p className="label-tag">Trips · {upcoming.length} upcoming</p>
        <Link to="/manual" className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ember hover:underline flex items-center gap-1.5" data-testid="triprows-add-btn">
          <Mail size={12} /> Paste email
        </Link>
      </div>

      {upcoming.length === 0 ? (
        <p className="px-5 py-8 text-sm text-sand-500">No upcoming trips. Forward a reservation to get started.</p>
      ) : (
        <div className="divide-y divide-sand-100">
          {upcoming.map((t) => <Row key={t.id} trip={t} />)}
        </div>
      )}

      {past.length > 0 && (
        <div className="border-t border-sand-200">
          <button
            onClick={() => setShowPast((v) => !v)}
            className="w-full px-5 py-3 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-sand-400 hover:text-sand-700 transition-colors"
            data-testid="toggle-past-trips"
          >
            {showPast ? "▾" : "▸"} Past · {past.length}
          </button>
          {showPast && (
            <div className="divide-y divide-sand-100 opacity-60" data-testid="past-trip-rows">
              {past.map((t) => <Row key={t.id} trip={t} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
